/**
 * MJ's magic-link provider plus one step: a session that starts for a different user than the one
 * the MJ client caches were written for clears those caches before Explorer's setup reads them,
 * and does not start at all if they cannot be cleared.
 *
 * Why: MJ 5.51 restores `CurrentUser` from the per-API-URL metadata snapshot on a warm start, so a
 * magic link redeemed in a browser that last ran someone else (the shared AGU SSO login, or an
 * earlier participant) boots as that previous user — 0 apps, denied self-heal creates, a stuck
 * loader (stage 2026-09-30, D1; upstream defect #15). See mj-cache-identity.ts for the trace and
 * for why the owner is a marker every provider reconciles (MJForgeRockProvider does the same).
 *
 * How: `initialize()` is Explorer's APP_INITIALIZER (app.module.ts `initializeAuth`) and the
 * workspace setup that reads the snapshot runs only after it (ng-explorer-app
 * explorer-app.component.js `setupAuth` → `handleLogin` → `initializeWorkspace`), so awaiting the
 * clear here is early enough. The clear is MJ's own logout clean slate
 * (`MJAuthBase.clearClientCaches`: localStorage except the theme keys + the `MJ_Metadata`
 * IndexedDB) followed by a delete that only resolves once the database is gone; the magic-link
 * token lives in sessionStorage and is untouched.
 *
 * Fail closed: if the clear fails, the session is not started. The user info stream errors with
 * MJCacheIdentityError, which Explorer's login screen shows (`setupAuth` → `classifyError` →
 * `userMessage`), and its Log in button signs out of this tab (a magic link has no interactive
 * login to offer instead).
 *
 * Registered under the same ClassFactory key as MJ's provider ('magic-link'); this module imports
 * `@memberjunction/ng-auth-services`, so its registration runs later and gets the higher
 * auto-incremented priority, and `AuthServicesModule.forRoot`'s
 * `ClassFactory.GetRegistration(MJAuthBase, 'magic-link')` resolves to it.
 */
import { Injectable } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { MJAuthBase, MJMagicLinkProvider, type StandardAuthError } from '@memberjunction/ng-auth-services';
import { ClassifyMJCacheIdentityError, DeleteIndexedDatabase, ReconcileMJCacheIdentity } from './mj-cache-identity';

@Injectable()
@RegisterClass(MJAuthBase, MJMagicLinkProvider.PROVIDER_TYPE)
export class AGUMagicLinkProvider extends MJMagicLinkProvider {
    private cacheIdentityFailed = false;

    override async initialize(): Promise<void> {
        await super.initialize();
        if (!this.isAuthenticated$.value) return;
        try {
            await ReconcileMJCacheIdentity(this.userInfo$.value?.email, {
                Storage: localStorage,
                ClearClientCaches: () => this.clearClientCaches(),
                DeleteDatabase: (name) => DeleteIndexedDatabase(name),
            });
        } catch (error) {
            console.error('[MagicLink] Not starting the session: the previous sign-in\'s MJ caches could not be cleared.', error);
            this.cacheIdentityFailed = true;
            this.updateAuthState(false);
            this.userInfo$.error(error);
        }
    }

    /** After a failed clear, the login screen's button signs out of this tab and reloads. */
    protected override async loginInternal(): Promise<void> {
        if (!this.cacheIdentityFailed) return super.loginInternal();
        await this.logout();
        this.reloadPage();
    }

    /** The reload after that sign-out; its own method so a spec can observe it (jsdom cannot reload). */
    protected reloadPage(): void {
        window.location.reload();
    }

    protected override classifyErrorInternal(error: unknown): StandardAuthError {
        return ClassifyMJCacheIdentityError(error) ?? super.classifyErrorInternal(error);
    }
}
